import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import {
  apiPresStatusToUiStatus,
  apiDownloadPresentation,
  formatFullName,
} from '../utils/participantMappers'
import type { ParticipantSummary } from '@proaxiv/server-api'
import { Button, ButtonLink } from './Button'
import { DetailsToggleButton } from './DetailsToggleButton'
import { ParticipantTicketButton } from './ParticipantTicketButton'
import {
  canAskForPresentation,
  canSendReminder,
  PRES_ELIGIBLE,
} from '../utils/presentationEligibility'

// ── Types ─────────────────────────────────────────────────────────────────────

type PresActionKind = 'ask' | 'accept' | 'reject'

// ── Icons ─────────────────────────────────────────────────────────────────────

const MailIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const UploadIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const DownloadIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const XIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const BellIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const UserCheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
)

const PenIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── Action button ─────────────────────────────────────────────────────────────
//
// Small square icon button used throughout this action strip. Built on top of the shared
// `Button`/`ButtonLink` components (via `variant="unstyled"`) so it always inherits the
// centralized interactive behavior — cursor-pointer, disabled state, focus ring — instead of
// each icon button independently (and inconsistently) reimplementing it.

function Btn({
  icon,
  label,
  onClick,
  href,
  colorClass,
  small = false,
}: {
  icon: React.ReactNode
  label: string
  /** When `href` is also set, receives the click event so a handler can `preventDefault()` and
   *  navigate itself once some async lookup resolves (e.g. the real ticket link). */
  onClick?: (e: React.MouseEvent) => void
  href?: string
  colorClass: string
  small?: boolean
}) {
  const cls = [
    'flex shrink-0 items-center justify-center rounded-lg transition-all duration-150',
    'focus-visible:ring-pax-blue/40 focus-visible:ring-2 focus-visible:outline-none',
    'active:scale-90',
    small ? 'h-6 w-6' : 'h-7 w-7',
    colorClass,
  ].join(' ')

  if (href) {
    return (
      <ButtonLink
        to={href}
        variant="unstyled"
        title={label}
        aria-label={label}
        className={cls}
        onClick={onClick}
      >
        {icon}
      </ButtonLink>
    )
  }

  return (
    <Button
      type="button"
      variant="unstyled"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cls}
    >
      {icon}
    </Button>
  )
}

// ── Presentation actions ────────────────────────────────────────────────────────

/** Props for the PresentationRowActions component. */
export interface PresentationRowActionsProps {
  reg: ParticipantSummary
  event: AppEvent
  onPresAction: (kind: PresActionKind, ids: string[]) => void
  onUpload: (participantId: string, participantName: string) => void
  /** Omitted when {@link presentationOnly} is set — the "Additional actions" section that
   *  would call this is hidden entirely in that mode. */
  onReminder?: (ids: string[]) => void
  /** Manually records this participant as attended, bypassing the check-in kiosk QR scan.
   *  Omitted when {@link presentationOnly} is set, same as {@link onReminder}. */
  onMarkAttended?: (participantId: string) => void
  /**
   * Restricts this panel to presentation actions only (ask / upload / review / download),
   * hiding the "Additional actions" section (participation reminder + ticket link) entirely.
   * Used on the Presentations archive page (#160), which is scoped purely to presentation
   * management — reminder/ticket actions belong to participant management, not this page.
   * Since there's then only one section, its "Presentation actions" heading is also dropped
   * as redundant. Defaults to `false` (both sections shown, as on the Accepted tab).
   */
  presentationOnly?: boolean
  /** Opens the private-note modal for this participant. Omitted when {@link presentationOnly}
   *  is set, same as {@link onReminder}/{@link onMarkAttended}. */
  onNote?: (participant: ParticipantSummary) => void
}

/**
 * Expanded Details panel actions: "Presentation actions" (ask / upload / review / download) and,
 * unless {@link PresentationRowActionsProps.presentationOnly} is set, "Additional actions"
 * (participation reminder + ticket link).
 */
export function PresentationRowActions({
  reg,
  event,
  onPresAction,
  onUpload,
  onReminder,
  onMarkAttended,
  presentationOnly = false,
  onNote,
}: PresentationRowActionsProps) {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const isEligible = PRES_ELIGIBLE.has(reg.role ?? '')
  const status = apiPresStatusToUiStatus(reg.presentation?.status)

  const canAsk = canAskForPresentation(reg)
  const canUpload = canAsk
  const canApprove = isEligible && status === 'uploaded'
  const canReject = canApprove
  const canDownload = status === 'uploaded' || status === 'accepted'
  const canRemind = canSendReminder(reg)
  const hasPresentationActions = canAsk || canUpload || canApprove || canReject || canDownload
  const participantName = formatFullName(reg.firstName, reg.lastName)

  async function handleDownload() {
    if (downloading || !reg.id) return
    setDownloading(true)
    setDownloadError(null)
    try {
      await apiDownloadPresentation(reg.id)
    } catch {
      setDownloadError(t('pres_err_download'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {hasPresentationActions && (
        <div>
          {/* When this is the only section (presentationOnly), the heading would just repeat
              the page's own "Presentations archive" framing — dropped as redundant. */}
          {!presentationOnly && (
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('acc_presentation_actions')}
            </span>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {canAsk && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<MailIcon />}
                onClick={() => onPresAction('ask', [reg.id!])}
              >
                {t('pres_action_ask')}
              </Button>
            )}
            {canUpload && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<UploadIcon />}
                onClick={() => onUpload(reg.id!, participantName)}
              >
                {t('pres_action_upload')}
              </Button>
            )}
            {canDownload && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<DownloadIcon />}
                disabled={downloading}
                onClick={() => void handleDownload()}
              >
                {t('pres_download')}
              </Button>
            )}
            {canApprove && (
              <Button
                variant="success"
                size="sm"
                leftIcon={<CheckIcon />}
                onClick={() => onPresAction('accept', [reg.id!])}
              >
                {t('pres_action_accept')}
              </Button>
            )}
            {canReject && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<XIcon />}
                onClick={() => onPresAction('reject', [reg.id!])}
              >
                {t('pres_action_reject')}
              </Button>
            )}
          </div>
          {downloadError && (
            <p role="alert" className="mt-1.5 text-xs text-red-600">
              {downloadError}
            </p>
          )}
        </div>
      )}

      {!presentationOnly && (
        <div>
          <span className="text-pax-gray text-xs font-semibold uppercase">
            {t('acc_additional_actions')}
          </span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {canRemind && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<BellIcon />}
                onClick={() => onReminder?.([reg.id!])}
              >
                {t('reminder_action_send')}
              </Button>
            )}
            {!reg.attendedAt && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<UserCheckIcon />}
                onClick={() => onMarkAttended?.(reg.id!)}
              >
                {t('acc_mark_attended')}
              </Button>
            )}
            <ParticipantTicketButton eventId={event.id} participantId={reg.id} />
            {onNote && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<PenIcon />}
                onClick={() => onNote(reg)}
              >
                {reg.organizerNote ? t('acc_note_action_edit') : t('acc_note_action_add')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────

/** Props for the AcceptedRowActions component. */
export interface AcceptedRowActionsProps {
  reg: ParticipantSummary
  expandedKeys: Set<string>
  onExpand: (id: string) => void
  onDelete: (id: string) => void
  /** Opens the "See conversation" history modal for this participant — see #158. */
  onConversation: (id: string) => void
  /** Render smaller buttons for mobile card footers. */
  small?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Compact, always-visible action strip for an accepted participant row: Details (expand toggle)
 * and Delete only. "Ask for presentation" and "Send reminder" live inside the row's expanded
 * Details panel instead (see {@link PresentationRowActions}) so the collapsed row stays minimal.
 */
export function AcceptedRowActions({
  reg,
  expandedKeys,
  onExpand,
  onDelete,
  onConversation,
  small = false,
}: AcceptedRowActionsProps) {
  const { t } = useTranslation()

  const isExpanded = expandedKeys.has(reg.id!)

  return (
    <div className="flex items-center gap-0.5">
      <DetailsToggleButton small={small} expanded={isExpanded} onClick={() => onExpand(reg.id!)} />
      <Btn
        small={small}
        icon={<ClockIcon />}
        label={t('conversation_action')}
        onClick={() => onConversation(reg.id!)}
        colorClass="text-pax-gray hover:bg-pax-purple/15"
      />
      <Btn
        small={small}
        icon={<TrashIcon />}
        label={t('reg_delete')}
        onClick={() => onDelete(reg.id!)}
        colorClass="text-pax-red hover:bg-pax-red/10"
      />
    </div>
  )
}
